import React, { FC, useState } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity } from 'react-native';
import { useSelector } from 'react-redux';
import i18n from 'i18n-js';
import ColorWrapper from '@styles/colors';
import { font } from '@styles/fonts';
import { scale } from '@components/frame';
import Button from '@styles/buttons';
import Svg from '@styles/icons/svg';
import { RootState } from '@store/types';
import type { ErrorFallbackProps } from './types';
import { FeatureFlagsAvailable } from '@components/featureFlags';

const ErrorFallback: FC<ErrorFallbackProps> = ({ C, error, errorInfo, screenName, sessionId, onRetry, onGoBack, onReportProblem }) => {
  const [showStack, setShowStack] = useState(false);
  const featureFlags = useSelector<RootState>((state) => state.user.featureFlags ?? {});
  const showStackTrace = FeatureFlagsAvailable('general-show-stack-trace', featureFlags);

  return (
    <View style={[styles.container, { backgroundColor: C('Background/Light/System [web]') }]} testID={"error-boundary-fallback"}>
      <View style={[styles.card, { backgroundColor: C('Background/Light/Secondary') }]}>
        <View style={styles.iconContainer}>
          <Svg icon="errorBig" width={scale(80)} height={scale(80)} color="Label/Light/Error" />
        </View>

        <Text
          testID={"error-boundary-title"}
          style={[font['Title 1'], styles.title, { color: C('Label/Light/Error') }]}
        >
          {i18n.t('Something went wrong')}
        </Text>

        <Text
          testID={"error-boundary-message"}
          style={[font['Body Large'], styles.subtitle, { color: C('Label/Light/Secondary') }]}
        >
          {i18n.t('This screen encountered an unexpected error')}
        </Text>

        {screenName && (
          <Text style={[font.Body, styles.screenName, { color: C('Label/Light/Tertiary') }]}>
            {i18n.t('Screen')}: {screenName}
          </Text>
        )}

        {sessionId && (
          <Text selectable testID={"error-boundary-session-id"} style={[font.Footnote, styles.sessionId, { color: C('Label/Light/Tertiary') }]}>
            {i18n.t('Session ID')}: {sessionId}
          </Text>
        )}

        <View style={styles.buttonRow}>
          <Button variant="L" type="Secondary" onPress={onGoBack}>
            {i18n.t('Go Back')}
          </Button>
          <Button variant="L" type="Primary" onPress={onRetry}>
            {i18n.t('Try Again')}
          </Button>
        </View>

        <View style={styles.reportRow}>
          <Button variant="L" type="Only Text Link" onPress={onReportProblem}>
            {i18n.t('Report to Smart Factory')}
          </Button>
        </View>

        {showStackTrace && error && (
            <View style={styles.devSection}>
              <TouchableOpacity onPress={() => setShowStack(!showStack)}>
                <Text style={[font['Button 2'], { color: C('Link/Light/Primary') }]}>
                  {showStack ? '▼' : '▶'} {i18n.t('Stack Trace')}
                </Text>
              </TouchableOpacity>

              {showStack && (
                <ScrollView
                  testID={"error-boundary-stack-trace"}
                  style={[styles.stackContainer, { backgroundColor: C('Background/Light/Field lighter') }]}
                >
                  <Text style={[font.Footnote, { color: C('Label/Light/Secondary') }]}>
                    {error.message}
                  </Text>
                  <Text style={[font.Footnote, styles.stackText, { color: C('Label/Light/Tertiary') }]}>
                    {errorInfo?.componentStack || error.stack}
                  </Text>
                </ScrollView>
              )}
            </View>
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: scale(32),
  },
  card: {
    padding: scale(48),
    borderRadius: scale(16),
    maxWidth: scale(560),
    width: '100%',
    alignItems: 'center',
  },
  iconContainer: {
    marginBottom: scale(24),
  },
  title: {
    marginBottom: scale(12),
    textAlign: 'center',
  },
  subtitle: {
    marginBottom: scale(12),
    textAlign: 'center',
  },
  screenName: {
    marginBottom: scale(16),
    textAlign: 'center',
  },
  buttonRow: {
    flexDirection: 'row',
    gap: scale(16),
    marginBottom: scale(12),
  },
  reportRow: {
    marginBottom: scale(20),
  },
  devSection: {
    width: '100%',
    marginTop: scale(24),
  },
  stackContainer: {
    marginTop: scale(12),
    padding: scale(16),
    borderRadius: scale(10),
    maxHeight: scale(280),
  },
  stackText: {
    marginTop: scale(8),
    fontFamily: 'monospace',
  },
  sessionId: {
    marginBottom: scale(32),
    textAlign: 'center',
  },
});

export default ColorWrapper(ErrorFallback);
